import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Briefcase, CalendarX, RefreshCw } from "lucide-react";
import { CrmIntegrationPanel } from "@/components/admin/CrmIntegrationPanel";

const TZ = "America/Toronto";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { timeZone: TZ, dateStyle: "medium", timeStyle: "short" }) + " ET" : "—");
// Interpret a datetime-local value as America/Toronto wall time and return UTC ISO (DST-aware).
function easternToUtc(local) {
  const [d, t] = local.split("T"); const [y, mo, da] = d.split("-").map(Number); const [h, mi] = t.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: TZ, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    .formatToParts(new Date(guess)).map((x) => [x.type, x.value]));
  const asTz = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
  return new Date(guess + (guess - asTz)).toISOString();
}
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BADGE = {
  moved: ["Moved by visitor", "bg-amber-100 text-amber-800 border-amber-300"],
  cancelled: ["Cancelled by visitor", "bg-rose-100 text-rose-800 border-rose-300"],
};
const VisitorBadge = ({ kind, testid }) => (
  <span className={`inline-flex items-center whitespace-nowrap text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full border ${BADGE[kind][1]}`} data-testid={testid}>{BADGE[kind][0]}</span>
);

function LeadDetail({ id, onChanged }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [owner, setOwner] = useState("");
  const [link, setLink] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(null);
  const load = () => api.get(`/admin/crm/leads/${id}`).then((r) => { setD(r.data); setOwner(r.data.lead.assigned_to || ""); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, label) => {
    setErr(null); setOk(null); setBusy(true);
    try { await fn(); await load(); onChanged(); setOk(label); }
    catch (e) { setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  if (!d) return <p className="text-sm text-slate-500">Loading…</p>;
  const { lead, activities, bookings } = d;
  const active = bookings.find((b) => b.status === "DEMO_BOOKED");
  const movedFrom = active?.replaces_booking_id && bookings.find((b) => b.id === active.replaces_booking_id);
  return (
    <div className="pf-card p-5" data-testid="crm-lead-detail">
      <h3 className="font-display text-xl text-slate-900 flex flex-wrap items-center gap-2">{lead.business_name}
        {lead.cancelled_by_visitor && <VisitorBadge kind="cancelled" testid="crm-detail-cancelled-badge" />}</h3>
      <p className="text-sm text-slate-600">{lead.contact_name} · {lead.job_title} · {lead.work_email} ({lead.email_type})</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-4">
        {[["Lead source", "Demo Page"], ["Demo status", lead.demo_status], ["Demo date/time", fmt(lead.scheduled_start_utc)],
          ["Timezone", lead.scheduling_timezone], ["Interests", (lead.demo_interests || []).join(", ") || "—"],
          ["Booking provider", lead.booking_provider || "—"], ["SalesCloser", lead.salescloser_status], ["Company size", lead.company_size || "—"],
          ["Website", lead.website], ["Industry", lead.industry], ["Phone", lead.phone || "—"]]
          .map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="text-slate-900 break-words" data-testid={`crm-field-${k.toLowerCase().replace(/\W+/g, "-")}`}>{v}</dd></div>)}
      </dl>
      <div className="flex flex-wrap gap-2 mt-4">
        <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Assign to (name or email)" data-testid="crm-owner-input" className="border rounded-lg px-2 py-1.5 text-sm" />
        <button type="button" className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50" data-testid="crm-owner-save" onClick={() => run(() => api.patch(`/admin/crm/leads/${id}`, { assigned_to: owner }), "Owner saved")} disabled={busy}>Save owner</button>
      </div>
      {active && (
        <div className="mt-4 border-t pt-4 space-y-2" data-testid="crm-active-booking">
          <p className="text-sm font-medium flex flex-wrap items-center gap-2">Active booking: {fmt(active.scheduled_start_utc)}
            {active.replaces_booking_id && <VisitorBadge kind="moved" testid="crm-detail-moved-badge" />}</p>
          {movedFrom && <p className="text-xs text-slate-600" data-testid="crm-moved-times">Was {fmt(movedFrom.scheduled_start_utc)} → now {fmt(active.scheduled_start_utc)}</p>}
          <div className="flex flex-wrap gap-2">
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https:// meeting link" data-testid="crm-meeting-link" className="border rounded-lg px-2 py-1.5 text-sm" />
            <button type="button" className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50" data-testid="crm-meeting-link-save" onClick={() => run(() => api.patch(`/admin/demo/bookings/${active.id}`, { meeting_url: link }), "Meeting link saved")} disabled={busy}>Save link</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {[["DEMO_COMPLETED", "Mark completed"], ["DEMO_NO_SHOW", "Mark no-show"], ["DEMO_CANCELLED", "Cancel demo"]].map(([s, l]) => (
              <button key={s} type="button" className="text-xs px-3 py-1.5 rounded-full border" data-testid={`crm-status-${s}`}
                onClick={() => run(() => api.patch(`/admin/demo/bookings/${active.id}`, { status: s }), "Status updated")} disabled={busy}>{l}</button>))}
          </div>
        </div>
      )}
      <div className="mt-4 border-t pt-4">
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note" data-testid="crm-note-input" className="w-full border rounded-lg px-2 py-1.5 text-sm" />
        <button type="button" className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 mt-1" data-testid="crm-note-save" disabled={!note.trim() || busy}
          onClick={() => run(async () => { await api.post(`/admin/crm/leads/${id}/notes`, { note }); setNote(""); }, "Note added")}>Add note</button>
      </div>
      {busy && <p className="text-xs text-slate-500 mt-2" data-testid="crm-busy">Saving…</p>}
      {ok && !busy && <p className="text-xs text-emerald-600 mt-2" data-testid="crm-ok">{ok}</p>}
      {err && <p className="text-sm text-rose-600 mt-2" data-testid="crm-error">{String(err)}</p>}
      <h4 className="text-sm font-semibold mt-5">Activity</h4>
      <ul className="mt-2 space-y-1.5 text-sm" data-testid="crm-activity-list">
        {activities.map((a) => <li key={a.id}><span className="text-xs text-slate-500">{fmt(a.at)}</span> · <strong>{a.type}</strong> · {a.actor}{a.note ? ` — ${a.note}` : ""}
          {a.type === "DEMO_MOVED_BY_VISITOR" && <span data-testid={`crm-activity-moved-${a.id}`}> — {fmt(a.details?.from_utc)} → {fmt(a.details?.to_utc)}</span>}</li>)}
      </ul>
    </div>
  );
}

function Availability() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ start: "", end: "", reason: "" });
  const [err, setErr] = useState(null);
  const load = () => api.get("/admin/demo/blocks").then((r) => setData(r.data)).catch(() => setErr("Could not load availability."));
  const act = async (fn) => { setErr(null); try { await fn(); await load(); } catch (e) { setErr(e?.response?.data?.detail || "Action failed"); } };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const wd = data.settings.weekdays;
  const toggleDay = (i) => act(() => api.put("/admin/demo/settings", { weekdays: { ...wd, [i]: !wd[i] } }));
  const saveWindow = (field, value) => act(() => api.put("/admin/demo/settings", { [field]: Number(value) }));
  const add = () => act(async () => {
    await api.post("/admin/demo/blocks", { start_utc: easternToUtc(form.start), end_utc: easternToUtc(form.end), reason: form.reason });
    setForm({ start: "", end: "", reason: "" });
  });
  return (
    <div className="pf-card p-5 mt-6" data-testid="crm-availability">
      <h3 className="font-medium text-slate-900 flex items-center gap-2"><CalendarX className="w-4 h-4" /> Demo availability (8:30 AM – 6:00 PM Eastern)</h3>
      <div className="flex flex-wrap gap-2 mt-3">{DAYS.map((d, i) => (
        <button key={d} type="button" onClick={() => toggleDay(String(i))} aria-pressed={!!wd[String(i)]} data-testid={`crm-weekday-${i}`}
          className={`text-xs px-3 py-1.5 rounded-full border ${wd[String(i)] ? "bg-slate-900 text-white" : ""}`}>{d}</button>))}</div>
      <div className="flex flex-wrap gap-4 mt-3 text-sm">
        <label className="flex items-center gap-2">Minimum notice (minutes)
          <input type="number" min={0} max={10080} defaultValue={data.settings.min_notice_minutes} key={`n${data.settings.min_notice_minutes}`}
            onBlur={(e) => Number(e.target.value) !== data.settings.min_notice_minutes && saveWindow("min_notice_minutes", e.target.value)}
            data-testid="crm-min-notice-input" className="border rounded-lg px-2 py-1 w-24" /></label>
        <label className="flex items-center gap-2">Book up to (days ahead)
          <input type="number" min={1} max={90} defaultValue={data.settings.max_advance_days} key={`a${data.settings.max_advance_days}`}
            onBlur={(e) => Number(e.target.value) !== data.settings.max_advance_days && saveWindow("max_advance_days", e.target.value)}
            data-testid="crm-max-advance-input" className="border rounded-lg px-2 py-1 w-20" /></label>
      </div>
      <p className="text-xs text-slate-500 mt-3">Blocked time — enter Eastern Time (America/Toronto):</p>
      <div className="flex flex-wrap gap-2 mt-1">
        <input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="crm-block-start" className="border rounded-lg px-2 py-1 text-sm" />
        <input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="crm-block-end" className="border rounded-lg px-2 py-1 text-sm" />
        <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (holiday, meeting…)" data-testid="crm-block-reason" className="border rounded-lg px-2 py-1 text-sm" />
        <button type="button" className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50" disabled={!form.start || !form.end || form.reason.length < 2} onClick={add} data-testid="crm-block-add">Block time</button>
      </div>
      <ul className="mt-3 text-sm space-y-1" data-testid="crm-block-list">{data.items.map((b) => (
        <li key={b.id}>{fmt(b.start_utc)} → {fmt(b.end_utc)} · {b.reason}
          <button type="button" className="text-xs underline ml-2" data-testid={`crm-block-remove-${b.id}`} onClick={() => act(() => api.delete(`/admin/demo/blocks/${b.id}`))}>Remove</button></li>))}</ul>
      {err && <p className="text-sm text-rose-600 mt-2" data-testid="crm-availability-error">{String(err)}</p>}
    </div>
  );
}

export default function CrmDemoLeads() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/crm/leads?source=DEMO_PAGE").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  return (
    <div className="min-w-0 max-w-[calc(100vw-3rem)] md:max-w-none" data-testid="crm-demo-leads">
      <div className="flex items-center justify-between">
        <div>
          <div className="pf-chip pf-chip-cyan"><Briefcase className="w-3 h-3" /> CRM</div>
          <h2 className="font-display text-3xl font-medium tracking-tight mt-3 text-slate-900">CRM – Demo leads</h2>
        </div>
        <button type="button" onClick={load} className="pf-btn text-sm flex items-center gap-1" data-testid="crm-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-5">
        <div className="pf-card p-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="crm-leads-table">
            <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-2">Business</th><th className="pr-2">Status</th><th className="pr-2">Demo</th><th>Owner</th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={4} className="py-4 text-slate-500" data-testid="crm-empty">No demo leads yet.</td></tr>}
              {items.map((l) => (
                <tr key={l.id} onClick={() => setSel(l.id)} className={`border-t cursor-pointer hover:bg-slate-50 ${sel === l.id ? "bg-cyan-50" : ""}`} data-testid={`crm-lead-row-${l.id}`}>
                  <td className="py-2 pr-2">{l.business_name}<div className="text-xs text-slate-500">{l.work_email}</div></td>
                  <td className="pr-2 text-xs">{l.demo_status}{(l.moved_by_visitor || l.cancelled_by_visitor) && <div className="mt-1 flex flex-wrap gap-1">
                    {l.moved_by_visitor && <VisitorBadge kind="moved" testid={`crm-moved-badge-${l.id}`} />}
                    {l.cancelled_by_visitor && <VisitorBadge kind="cancelled" testid={`crm-cancelled-badge-${l.id}`} />}</div>}</td><td className="pr-2 text-xs">{fmt(l.scheduled_start_utc)}</td><td className="text-xs">{l.assigned_to || "Unassigned"}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
        {sel ? <LeadDetail id={sel} onChanged={load} /> : <div className="pf-card p-5 text-sm text-slate-500">Select a lead to see details.</div>}
      </div>
      <Availability />
      <CrmIntegrationPanel />
    </div>
  );
}
